import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { cloneDeep } from 'lodash';

import GameEndTemplate from '../../components/Templates/Game/GameEndTemplate/GameEndTemplate';
import GameStartTemplate from '../../components/Templates/Game/GameStartTemplate/GameStartTemplate';
import GameplayTemplate from '../../components/Templates/Game/GameplayTemplate/GameplayTemplate';
import { PageTransition } from '../../components/Molecules';
import useAbortControllerSignal from '../../hooks/useAbortControllerSignal';
import useAudioPlayer from '../../hooks/useAudioPlayer';
import { Balance } from '../../models/Base';
import { GamePlayState, GameScreen } from '../../models/GameControllerStore';
import {
  TextOrderingAnswer,
  TextOrderingUnitType,
} from '../../models/GameStore';
import { Profile } from '../../models/UserStore';
import { getGameTypeDetails } from '../../constants/Games/gameType';
import {
  GAME_MODE_SETUP_FN,
  GameMode,
  useGameController,
  useGameControllerStore,
} from '../../stores/GameControllerStore';
import { useGameStore } from '../../stores/GameStore';
import { useRecordStore } from '../../stores/RecordStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore } from '../../stores/SpaceStore';
import { useTimer, useTimerState } from '../../stores/TimeStore';
import { useUserStore } from '../../stores/UserStore';
import { getTimeString } from '../../utils/date';

import './Game.css';

const Game = () => {
  const navigate = useNavigate();
  const isLoggedIn = useSessionStore((state) => state.isLoggedIn);
  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/start');
    }
  }, [isLoggedIn, navigate]);

  const { classroomId = '', gameSetId = '', gameId = '' } = useParams();
  const query = new URLSearchParams(useLocation().search);
  const mode = query.get('mode');

  const { t } = useTranslation();
  const signal = useAbortControllerSignal();
  const audioPlayer = useAudioPlayer();
  const timer = useTimer('game');

  useEffect(() => {
    return () => {
      useGameControllerStore.getState().cleanup();
      useRecordStore.getState().syncPlayingRecord().catch(console.error); // FIXME: no more playing records because await when init
    };
  }, []);

  useEffect(() => {
    useUserStore.getState().profiles.list('gamePlay').addListener();
    useGameStore.getState().gameCollections.list('gamePlay').addListener();
    useSpaceStore.getState().spaces.list('gamePlay').addListener();
    useSpaceStore.getState().albums.list('gamePlay').addListener();
    useGameStore.getState().games.list('gamePlay').addListener();
    useRecordStore.getState().gameRecords.list('gamePlay').addListener();
    useGameStore.getState().gameStatistics.list('gamePlay').addListener();
    return () => {
      useUserStore.getState().profiles.list('gamePlay').removeListener();
      useSpaceStore.getState().spaces.list('gamePlay').removeListener();
      useSpaceStore.getState().albums.list('gamePlay').removeListener();
      useGameStore.getState().gameCollections.list('gamePlay').removeListener();
      useGameStore.getState().games.list('gamePlay').removeListener();
      useRecordStore.getState().gameRecords.list('gamePlay').removeListener();
      useGameStore.getState().gameStatistics.list('gamePlay').removeListener();
    };
  }, []);

  useEffect(() => {
    // TODO: stop time manangement (haltModal)
  }, []);

  useEffect(() => {
    const {
      extractDisplayData,
      prepareIntermediate,
      fetchData,
      refineResult,
      initializeRecord,
      finalizeRecord,
    } = GAME_MODE_SETUP_FN[GameMode.Default];
    const userId = useSessionStore.getState().activeUserId as string;
    const locale = useUserStore.getState().getAccount(userId)?.settings
      .interfaceLocale;
    useGameControllerStore
      .getState()
      .setup(
        { t, mode: GameMode.Default, locale, userId, signal },
        {
          classroomId,
          gameSetId,
          gameId,
          isAssistedGame: mode === 'tutorial',
        },
        extractDisplayData,
        prepareIntermediate,
        fetchData,
        refineResult,
        initializeRecord,
        finalizeRecord,
        audioPlayer,
        timer,
      )
      .then(() => {
        const { error } = useGameControllerStore.getState();
        if (error) {
          console.warn(error.message);
        }
      })
      .catch(console.error);
  }, [classroomId, gameSetId, gameId, mode, t, signal, audioPlayer, timer]);

  const error = useGameControllerStore((state) => state.error);
  const gameScreen = useGameControllerStore((state) => state.gameScreen);

  if (error) {
    return <div className="game-screen">{error.message}</div>;
  }

  return (
    <PageTransition>
      <div className="game-screen">
        {gameScreen === GameScreen.GameStart && <GameStart />}
        {gameScreen === GameScreen.GamePlay && <GamePlay />}
        {gameScreen === GameScreen.GameEnd && <GameEnd />}
      </div>
    </PageTransition>
  );
};

const GameStart = () => {
  const activeUserId = useSessionStore((store) => store.activeUserId);
  const profile = useUserStore((state) => state.getProfile(activeUserId ?? ''));

  const {
    // clubIcon,
    // clubName,
    gameTitle,
    typeName,
    latestScore,
    latestTime,
    playCount,
    avgScore,
    reward,
    isAssistedGame,
  } = useGameControllerStore((store) => {
    return {
      classroomId: store.params.classroomId,
      clubIcon: store.displayData?.context.icon,
      clubName: store.displayData?.context.title,
      gameTitle: store.displayData?.body.title ?? 'Game',
      // gameModeName: store.displayData?.body.modeName,
      typeName: store.displayData?.body.gameIconTag ?? 'spelling',
      latestScore: store.displayData?.latestRecord?.score,
      latestTime: store.displayData?.latestRecord?.durationInMs,
      playCount: store.displayData?.statistics?.playCount,
      avgScore: store.displayData?.statistics?.averageScore,
      reward: store.displayData?.maximumReward,
      isAssistedGame: store.params.isAssistedGame,
    };
  });

  return profile ? (
    <GameStartTemplate
      gameStartInfoBoardProps={{
        // clubIcon,
        // clubName,
        gameTitle,
        gameIcon: getGameTypeDetails(typeName).icon,
        gameModeName: isAssistedGame ? 'Gamified Tutorial' : 'AI Memory Game',
        typeName: getGameTypeDetails(typeName).displayName,
        latestScore,
        latestTime,
        playCount,
        avgScore,
      }}
      gameStartVSBoardProps={{
        profile: profile,
        opponentRecords: [],
        reward: reward ? new Balance(reward) : undefined,
      }}
      onClickStart={() => {
        useGameControllerStore.getState().onGameStart().catch(console.error);
      }}
    />
  ) : (
    <></>
  );
};

const GamePlay = () => {
  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );

  const {
    isAssistedGame,
    gamePlayState,
    instruction,
    answer,
    userScore,
    typeName,
    opponentProfile,
    opponentScore,
    evaluationForImprovement,
  } = useGameControllerStore((store) => {
    const a = cloneDeep(
      store.gameSequence?.[store.index ?? 0]?.gameData
        .answer as TextOrderingAnswer,
    );
    const evaluation = store.evaluationForImprovement;
    evaluation?.forEach((word, i) => {
      word.units.forEach((unit, j) => {
        if (unit.isBlank && (unit.score ?? 0) > 95) {
          if (a.words[i]?.units[j]?.type) {
            a.words[i]!.units[j]!.type = TextOrderingUnitType.Hint;
          }
        }
      });
    });

    return {
      isAssistedGame: store.params.isAssistedGame,
      gamePlayState: store.gamePlayState,
      instruction:
        store.gameSequence?.[store.index ?? 0]?.gameData.instruction.text,
      answer: a,
      userScore: store.score,
      typeName: store.displayData?.body.gameIconTag ?? 'spelling',
      opponentProfile: store.displayData?.dummies?.profiles[0],
      opponentScore: store.displayData?.dummies?.opponents[0]?.score,
      evaluationForImprovement: evaluation,
    };
  });

  const gameController = useGameController();

  const timer = useTimerState('game', Math.floor(1000 / 24));

  useEffect(() => {
    if (gamePlayState === GamePlayState.End) {
      useGameControllerStore
        .getState()
        .onGameStageEnd(timer.overallTimeElapsed)
        .catch(console.error);
    }
  }, [gamePlayState, timer.overallTimeElapsed]);

  const profiles: Profile[] = [];
  const scores: number[] = [];
  if (userProfile) {
    profiles.push(userProfile);
    scores.push(userScore);
    if (!isAssistedGame && evaluationForImprovement === undefined) {
      profiles.push(opponentProfile ?? userProfile);
      scores.push(opponentScore ?? 50);
    }
  }

  return (
    <GameplayTemplate
      gameHudProps={{
        profiles,
        gameType: typeName,
        scores,
        elapsedTime: getTimeString(timer.overallTimeElapsed, 'm:ss.SS'),
        instruction: instruction,
      }}
      memoryGameMainGroupProps={{
        answer,
        gameController,
        isAssistedGame,
      }}
    />
  );
};

const GameEnd = () => {
  const navigate = useNavigate();

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userProfile = useUserStore((state) =>
    state.getProfile(activeUserId ?? ''),
  );

  const {
    userScore,
    userDurationInMs,
    opponentProfile,
    opponentScore,
    opponentDurationInMs,
    evaluationForImprovement,
  } = useGameControllerStore((store) => {
    const isAssistedGame = store.params.isAssistedGame;
    const wasImprovementGame =
      store.improvementPlayCount + store.fullPlayCount > 0;
    return {
      userScore: store.record?.score,
      userDurationInMs: store.record?.durationInMs,
      opponentProfile:
        isAssistedGame || wasImprovementGame
          ? undefined
          : store.displayData?.dummies?.profiles[0],
      opponentScore:
        isAssistedGame || wasImprovementGame
          ? undefined
          : store.displayData?.dummies?.opponents[0]?.score,
      opponentDurationInMs:
        isAssistedGame || wasImprovementGame
          ? undefined
          : store.displayData?.dummies?.opponents[0]?.durationInMs,
      evaluationForImprovement: store.evaluationForImprovement,
    };
  });

  let buttonText = 'Next';
  if (evaluationForImprovement) {
    buttonText = 'Play Improvement Game';
  } else if (evaluationForImprovement === null) {
    buttonText = 'Play Full Game';
  }

  return userProfile ? (
    <GameEndTemplate
      gameEndVSResultGroupProps={{
        currentUserProfile: userProfile,
        currentUserScore: userScore ?? 100,
        currentUserDuration: getTimeString(
          userDurationInMs ?? 10000,
          'm:ss.SS',
        ),
        opponentUserProfile: opponentProfile,
        opponentUserScore: opponentScore,
        opponentUserDuration: opponentDurationInMs
          ? getTimeString(opponentDurationInMs, 'm:ss.SS')
          : undefined,
      }}
      buttonText={buttonText}
      onClickNext={() => {
        if (evaluationForImprovement === undefined) {
          useGameControllerStore.getState().onLeaveGame(navigate);
        } else {
          useGameControllerStore.getState().onGameStart().catch(console.error);
        }
      }}
    />
  ) : (
    <></>
  );
};

export default Game;
